<template lang="pug">
  .wrap
    .header-wrapper
      page-title.report-title(fontSize='30px' :wrapStyle='{ marginBottom: "10px" }')
        | Your course report:
      .export-buttons
        a(:href="reportCsvUrl")
          el-button Export as CSV

    .user-filters
      el-form(@submit.native.prevent='search')
        el-row(:gutter='20')
          el-col(:sm='6')
            .filter-label Search users
            el-input(v-model='searchParams.q' @keyup.native.enter="search" :on-icon-click='search' icon="search")
          el-col(:sm='8')
            .filter-label Groups
            el-select(v-model="searchParams.groups" multiple filterable placeholder="All")
              el-option(v-for="group in searchOptions.groups" :key="group.id" :label="group.name" :value="group.id")

          el-col(:sm='6')
            .filter-label Status
            el-select(v-model="searchParams.status" multiple placeholder="All")
              el-option(v-for="status in searchOptions.status" :key="status.id" :label="status.name" :value="status.id")

          el-col(:sm='4')
            el-button.submit-filters(native-type="submit") Filter
            el-button.submit-filters(@click="resetFilters") Reset

    el-table(:data="usersReport.list.results" empty-text="No entries" v-loading='usersReport.loading')
      el-table-column(label="Name" prop='name' class-name='title-cell' min-width="300")
        template(slot-scope="scope")
          p {{ scope.row.firstName }} {{ scope.row.lastName }}
          small {{ scope.row.email }}

      el-table-column(label="Attempts" prop='type' align='center' width="200")
        template(slot-scope="scope")
          | {{ scope.row.numOfAttempts }}

      //- el-table-column(label="Time" width='200' align='center')
      //-   template(slot-scope="scope")
      //-     | 00:00

      el-table-column(label="Status" width='200' align='center')
        template(slot-scope="scope")
          | {{ getResultEntry(scope.row).status || '-' }}

      el-table-column(label="Score" width='200' align='center')
        template(slot-scope="scope")
          | {{ getResultEntry(scope.row).score || '-' }}

    .pages
      el-pagination(layout="prev, pager, next" @current-change='changePage' :total="usersReport.list.count" :page-size='pageSize')


</template>

<script>
import _ from "lodash";
import ChartWrap from "@/shared/components/ChartWrap";
import CrudMixin from "@/shared/mixins/Crud";
import ListHeader from "@/shared/components/ListHeader";
import PageTitle from "@/shared/components/layout/PageTitle";
import TimeSwitcher from "@/brapp/components/shared/TimeSwitcher";
import CircularCounter from "@/shared/components/CircularCounter";
import LineChart from "./LineChart";
import DoughnutChart from "./DoughnutChart";
const padTime = value => _.padStart(value, 2, "0");

const INITIAL_SEARCH_PARAMS = {
  q: "",
  groups: [],
  status: []
};

export default {
  components: {
    PageTitle,
    ListHeader,
    TimeSwitcher,
    CircularCounter,
    LineChart,
    DoughnutChart,
    ChartWrap
  },
  mixins: [CrudMixin],
  data() {
    return {
      pageSize: 150,
      searchOptions: {
        groups: [],
        status: [
          { id: "passed", name: "Passed" },
          { id: "failed", name: "Failed" },
          { id: "incomplete", name: "Incomplete" }
        ]
      },
      searchParams: _.cloneDeep(INITIAL_SEARCH_PARAMS),
      usersReport: {
        loading: true,
        list: {
          results: [],
          count: 0
        }
      }
    };
  },
  computed: {
    courseId() {
      return this.$route.params.courseId;
    },
    reportCsvUrl() {
      const params = {
        ...this.searchParams,
        format: "csv",
        company: this.$store.getters["auth/activeCompany"].id
      };
      return this.$store.getters["api/generateUrl"]({
        path: `/itrain/courses/${this.courseId}/users-report/`,
        params
      });
    }
  },
  watch: {
    dateRange(newVal) {
      this.onRangeChange(newVal);
    }
  },
  methods: {
    async loadUsersReport({ params = {} } = {}) {
      const mergedParams = {
        ...params,
        pageSize: this.pageSize
      };

      this.usersReport.loading = true;
      this.usersReport.list = await this.$store.dispatch("api/sendRequest", {
        method: "get",
        url: `/itrain/courses/${this.courseId}/users-report/`,
        params: mergedParams
      });

      this.usersReport.loading = false;
    },
    async loadGroupOptions() {
      const results = await this.$store.dispatch("api/sendRequest", {
        method: "get",
        url: "/app-license-groups/",
        params: {
          app_license__app__codename: "itrain",
          company: this.$store.getters["auth/activeCompany"].id
        }
      });

      this.searchOptions.groups = _.orderBy(
        results.map(appLicenseGroup => ({
          id: appLicenseGroup.id,
          name: appLicenseGroup.group.name
        })),
        [entry => entry.name.toLowerCase()]
      );
    },
    getResultEntry(row) {
      if (row.latestSuccessAttempt.length) {
        return {
          score: row.latestSuccessAttempt[0].score,
          status: row.latestSuccessAttempt[0].scormStatus
        };
      }

      if (row.latestFailedAttempt.length) {
        return {
          score: row.latestFailedAttempt[0].score,
          status: row.latestFailedAttempt[0].scormStatus
        };
      }

      return {
        score: "-",
        status: "incomplete"
      };
    },
    secondsToMinuteSeconds(d) {
      if (!d) {
        return "00:00";
      }
      const minutes = Math.floor((d % 3600) / 60);
      const seconds = Math.floor((d % 3600) % 60);
      return `${padTime(minutes)}:${padTime(seconds)}`;
    },
    changePage(page) {
      const params = {
        ...this.searchParams,
        page
      };
      this.loadUsersReport({ params });
    },
    search() {
      const params = {
        ...this.searchParams,
        company: this.$store.getters["auth/activeCompany"].id
      };
      this.loadUsersReport({ params });
    },
    resetFilters() {
      this.searchParams = _.cloneDeep(INITIAL_SEARCH_PARAMS);
      this.search();
    }
  },
  created() {
    this.loadUsersReport();
    this.loadGroupOptions();
  }
};
</script>

<style scoped lang="scss">
@import "../../../../../assets/scss/mixins.scss";
.stat-cell .cell {
  word-break: normal;
  line-height: 15px;
  padding: 10px 0;
}
.overall-stats {
  display: flex;
  width: 100%;
  justify-content: space-between;
  margin-bottom: 30px;
  .stat-panel {
    flex-basis: calc(25% - 10px);
    background: white;
    padding: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    .num {
      font-size: 40px;
      margin-bottom: 5px;
    }
    .label {
      font-size: 12px;
    }
  }
  @include MQ(M) {
    flex-wrap: wrap;
    .stat-panel {
      flex-basis: calc(50% - 10px);
      margin-bottom: 20px;
    }
  }
  @include MQ(S) {
    flex-direction: column;
    justify-content: flex-start;
    .stat-panel {
      flex-basis: auto;
      margin-bottom: 10px;
    }
  }
}

.pages {
  margin-top: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.user-filters {
  background: white;
  padding: 20px;
  margin-bottom: 10px;
}

.submit-filters {
  margin-top: 25px;
}

.filter-label {
  font-size: 14px;
  margin-bottom: 8px;
}

.header-wrapper {
  display: flex;
  margin-bottom: 20px;
}

.export-buttons {
  margin-left: auto;
}
</style>
